const steps = [
  {
    step: '01',
    icon: 'ri-map-2-line',
    title: 'Wind Resource Assessment',
    description: "A wind resource study evaluates your site's wind speed, direction, and consistency using meteorological data and on-site measurement. This determines whether your location is viable for wind energy and what capacity factor to expect.",
    detail: '12-month anemometry recommended',
    color: 'bg-secondary-500',
  },
  {
    step: '02',
    icon: 'ri-file-text-line',
    title: 'Planning & Approvals',
    description: "Wind projects require state planning approvals, environment impact assessments, aviation lighting compliance, and grid connection studies. AK Energy's network of wind development specialists navigates the full regulatory pathway.",
    detail: 'End-to-end development support',
    color: 'bg-primary-500',
  },
  {
    step: '03',
    icon: 'ri-flashlight-line',
    title: 'Grid Connection & PPA',
    description: "Commercial wind projects connect to the National Electricity Market (NEM) or operate under a Power Purchase Agreement directly with an industrial off-taker. Corporate PPAs fix your energy price for 10–25 years, providing long-term cost certainty.",
    detail: 'Fixed-price energy contracts available',
    color: 'bg-secondary-500',
  },
  {
    step: '04',
    icon: 'ri-settings-3-line',
    title: 'Construction & Operation',
    description: "Wind turbine installation typically takes 12–24 months from financial close to first generation. Once operational, modern wind farms run for 25+ years with minimal maintenance, delivering consistent returns and clean energy for decades.",
    detail: '25+ year operational lifespan',
    color: 'bg-primary-500',
  },
];

const howItWorks = [
  {
    icon: 'ri-windy-line',
    title: 'Wind Turns the Blades',
    description: 'Wind pushes the aerodynamic rotor blades, causing the hub to rotate. Blade pitch control systems optimise the angle for different wind speeds.',
  },
  {
    icon: 'ri-settings-line',
    title: 'Generator Produces Power',
    description: 'The rotating hub drives a generator (directly or via a gearbox) to produce AC electricity. Modern turbines generate 3–6 MW at rated wind speed.',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Transformer Steps Up Voltage',
    description: "Each turbine's transformer steps voltage up for efficient transmission to the substation, which then connects to the grid or directly to an industrial off-taker.",
  },
  {
    icon: 'ri-home-wifi-line',
    title: 'Electricity Reaches the Grid',
    description: "Power flows to the National Electricity Market or a private buyer's facility. SCADA systems monitor every turbine in real time for optimal performance.",
  },
];

export default function WindProcess() {
  return (
    <section className="bg-background-50 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">

        {/* How wind works */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-50 border border-secondary-200/50 mb-5">
            <i className="ri-question-line text-secondary-600 text-sm"></i>
            <span className="text-xs font-semibold text-secondary-700 tracking-wide uppercase whitespace-nowrap">How Wind Energy Works</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4">
            From Wind to Watts:<br className="hidden md:block" /> How Wind Turbines Generate Power
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Modern wind turbines are sophisticated machines that convert kinetic wind energy into grid-quality electricity with remarkable efficiency.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {howItWorks.map((item, idx) => (
            <div key={item.title} className="relative bg-background-100/70 rounded-2xl p-6 border border-background-200/70">
              <div className="absolute top-4 right-4 font-heading text-4xl font-extrabold text-background-200 select-none">
                {String(idx + 1).padStart(2, '0')}
              </div>
              <div className="w-11 h-11 rounded-xl bg-secondary-50 flex items-center justify-center mb-4">
                <i className={`${item.icon} text-lg text-secondary-600`}></i>
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-900 mb-2">{item.title}</h3>
              <p className="text-xs text-foreground-600 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>

        {/* Development pathway */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200/50 mb-5">
            <i className="ri-road-map-line text-primary-500 text-sm"></i>
            <span className="text-xs font-semibold text-primary-700 tracking-wide uppercase whitespace-nowrap">Development Pathway</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4">
            From Site to Generating:<br className="hidden md:block" /> The Wind Project Journey
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Wind projects follow a structured development pathway. AK Energy connects you with specialists for each stage.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-12 left-[calc(12.5%)] right-[calc(12.5%)] h-px bg-gradient-to-r from-secondary-200 via-primary-300 to-secondary-200 z-0" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 relative z-10">
            {steps.map((step, idx) => (
              <div key={step.step} className="flex flex-col items-center text-center" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className={`w-24 h-24 rounded-2xl ${step.color} flex flex-col items-center justify-center mb-6`}>
                  <i className={`${step.icon} text-2xl text-white mb-1`}></i>
                  <span className="text-xs font-bold text-white/70">{step.step}</span>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-3">{step.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">{step.description}</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-secondary-600">
                  <i className="ri-check-double-line"></i>
                  {step.detail}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 text-center">
          <a
            href="/#contact"
            className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold rounded-xl bg-secondary-500 text-white hover:bg-secondary-600 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-windy-line text-lg"></i>
            Talk to a Wind Energy Specialist
          </a>
        </div>
      </div>
    </section>
  );
}